/// <amd-module name="Search/Coordinator/SeedDataSearchService" />
import { IVssPageContext,
         IVssObservableService,
         VssObservableService,
         Services,
         ServiceOptions } from "VSS/Platform/Context";
import { IVssPageService } from "VSS/Platform/Page";
import { ITfsSearchQuery } from "Tfs/Platform/Search";
import { WorkItemSearchRequest, WorkItemSearchResponse } from "Search/LegacyWebApi/Contracts/WorkItem";
import * as Contracts from "Search/LegacyWebApi/Contracts/Search.SharedLegacy";
import * as Rest_Client from "Search/LegacyWebApi/RestClient/Search";

/**
* Events raised by SeedDataSearchService
*/
export const enum SearchServiceEventNames {
    SearchEntityResultsFetched = "SearchEntityResultsFetched",
}

/**
* Failed state details for the search data fetch operation
*/
export interface ISearchServiceFailedState {
    error: any;
    query: WorkItemSearchRequest;
    activityId: string;
}

/**
* State of the search data fetch operation
*/
export interface ISearchServiceState {
    responseWithActivityId?: Contracts.EntitySearchResponseWithActivityId;
    error?: ISearchServiceFailedState;
    searchInProgress: boolean;
    searchText?: string;
}

/**
 * ISeedDataSearchService is used to fetch search results in parallel to FPS
 */
export interface ISeedDataSearchService extends IVssObservableService<ISearchServiceState> {
    fetchSearchResults(projectId: string | undefined, searchQuery: ITfsSearchQuery):Promise<void>;
    getSearchServiceState(): ISearchServiceState;
    resetState(): void;
}

/**
 * SeedDataSearchService is used to fetch search results in parallel to FPS. It is a persistant service
 * It is implemented only for workitem - based on performance impact, it will be implemented for other entities.
 */
export class SeedDataSearchService extends VssObservableService<ISearchServiceState> implements ISeedDataSearchService {
    private state: ISearchServiceState;

    public _serviceStart(pageContext: IVssPageContext): void {
        super._serviceStart(pageContext);
        this.resetState();
    }

    public fetchSearchResults(projectId: string | undefined, searchQuery: ITfsSearchQuery): Promise<void> {
        // setting state properties to undefined before making a call to search service
        this.state =  { searchInProgress: true, searchText: searchQuery.queryText };

        const workItemSearchRestClient = this.pageContext.getRestClient<Rest_Client.ISearchRestClient>("Legacy.ISearchRestClient");
        let filters: { [key: string]: string[] } = {};

        // Build the explicit filter parameter list.
       if (searchQuery.filters) {
            for (const key in searchQuery.filters) {
                const filterValue: string[] | undefined = searchQuery.filters[key];
                if (filterValue) {
                    filters[key] = filterValue;
                }
            }
        }

        const request: WorkItemSearchRequest = {
                searchText: searchQuery.queryText,
                skipResults: 0,
                takeResults: searchQuery.pageSize ? searchQuery.pageSize : 50,
                sortOptions: [],
                summarizedHitCountsNeeded: searchQuery.includeFacets ? searchQuery.includeFacets : true,
                searchFilters: filters,
                filters: [],
                includeSuggestions: false
            };

        return workItemSearchRestClient
            .postWorkItemQuery(request, projectId)
            .then(responseWithActivityId => {
                const workItemSearchResponse = responseWithActivityId.response as WorkItemSearchResponse;
                if (this.state.searchText === workItemSearchResponse.query.searchText) {
                    this.state = { responseWithActivityId, searchInProgress: false, searchText: searchQuery.queryText };
                    this._notify(this.state, SearchServiceEventNames.SearchEntityResultsFetched);
                }
            }, error => {
                const pageData = this.pageContext.getService<IVssPageService>("IVssPageService").getData();
                const searchFailedState: ISearchServiceFailedState = { error, query: request, activityId: pageData && pageData.sessionId ? pageData && pageData.sessionId : ""};
                this.state = { error: searchFailedState, searchInProgress: false, searchText: searchQuery.queryText };
                this._notify(this.state, SearchServiceEventNames.SearchEntityResultsFetched);
            });
    }

    public getSearchServiceState(): ISearchServiceState {
        return this.state;
    }

    public resetState(): void {
        this.state = {
            responseWithActivityId: undefined,
            error: undefined,
            searchInProgress: false,
            searchText: undefined
        };
    }
}

Services.add("ISeedDataSearchService", { serviceFactory: SeedDataSearchService, options: ServiceOptions.Persistant });